/**
 * A RÉGUA — ferramenta de medir sobre uma figura.
 *
 * Uma régua graduada (em geral centímetros e milímetros) por cima da cena. O aluno a
 * arrasta pelo corpo e a gira pela bolinha na ponta; com a régua em foco, as setas a
 * movem um milímetro (com Shift, um centímetro) e Q e E a giram um grau (com Shift,
 * quinze). A régua mede a FIGURA, e não a tela: a graduação está nas unidades do
 * desenho, e continua certa com qualquer zoom.
 *
 * A borda graduada é a de cima, com o zero perto da ponta esquerda — como numa régua
 * de verdade, há uma sobra antes do zero. É ela que torna possível o erro de começar a
 * medir pela ponta da régua, ou pelo 1.
 */

import { formatBR } from '../core/num.ts';
import { conta } from '../core/pt.ts';
import type { Vec2 } from '../geom/vec.ts';
import { conversor } from './coords.ts';
import type { Rect } from '../render/scene.ts';
import type { MeasurableFigure } from '../schema/item.ts';

const NS = 'http://www.w3.org/2000/svg';

export interface Regua {
  posicao(): { readonly inicio: Vec2; readonly angulo: number };
  dispose(): void;
}

export function criarRegua(
  svg: SVGSVGElement,
  vb: Rect,
  spec: MeasurableFigure['regua'],
  aoSoltar?: (angulo: number) => void,
): Regua {
  const E = spec.escala;
  const L = spec.comprimento * E;
  const sobra = 0.55 * E;
  const larg = 0.9 * E;
  let pos: Vec2 = { x: spec.inicio.x, y: spec.inicio.y };
  let ang = spec.angulo;

  const { yTela, paraModelo } = conversor(svg, vb);
  const el = <K extends keyof SVGElementTagNameMap>(
    tag: K,
    cls: string,
  ): SVGElementTagNameMap[K] => {
    const e = document.createElementNS(NS, tag);
    e.setAttribute('class', cls);
    return e;
  };

  const g = el('g', 'regua');
  g.setAttribute('tabindex', '0');
  g.setAttribute('role', 'group');

  const corpo = el('rect', 'regua-corpo');
  corpo.setAttribute('x', String(-sobra));
  corpo.setAttribute('y', '0');
  corpo.setAttribute('width', String(L + 2 * sobra));
  corpo.setAttribute('height', String(larg));
  corpo.setAttribute('rx', String(0.08 * E));
  g.appendChild(corpo);

  const sub = spec.subdivisoes;
  const n = spec.comprimento * sub;
  for (let i = 0; i <= n; i++) {
    const x = (i * E) / sub;
    const inteiro = i % sub === 0;
    const meio = sub % 2 === 0 && i % (sub / 2) === 0;
    const traco = el('line', inteiro ? 'regua-traco regua-traco-inteiro' : 'regua-traco');
    traco.setAttribute('x1', String(x));
    traco.setAttribute('y1', '0');
    traco.setAttribute('x2', String(x));
    traco.setAttribute('y2', String(larg * (inteiro ? 0.42 : meio ? 0.3 : 0.18)));
    g.appendChild(traco);
    if (inteiro) {
      const t = el('text', 'regua-num');
      t.setAttribute('x', String(x));
      t.setAttribute('y', String(larg * 0.72));
      t.setAttribute('text-anchor', 'middle');
      t.setAttribute('dominant-baseline', 'middle');
      t.textContent = String(i / sub);
      g.appendChild(t);
    }
  }
  const unidade = el('text', 'regua-unidade');
  unidade.setAttribute('x', String(-sobra * 0.55));
  unidade.setAttribute('y', String(larg * 0.72));
  unidade.setAttribute('text-anchor', 'middle');
  unidade.setAttribute('dominant-baseline', 'middle');
  unidade.textContent = spec.unidade;
  g.appendChild(unidade);

  const giro = el('circle', 'regua-giro');
  giro.setAttribute('cx', String(L + sobra + 0.38 * E));
  giro.setAttribute('cy', String(larg / 2));
  giro.setAttribute('r', String(0.24 * E));
  g.appendChild(giro);
  svg.appendChild(g);

  function desenhar(): void {
    g.setAttribute('transform', `translate(${pos.x} ${yTela(pos.y)}) rotate(${-ang})`);
    g.setAttribute(
      'aria-label',
      `Régua de ${spec.comprimento} ${spec.unidade}, girada ${conta(formatBR(Math.round(ang * 10) / 10), 'grau')}. ` +
        'Arraste para mover e gire pela bolinha da ponta; com a régua selecionada, as setas a movem e as teclas Q e E a giram.',
    );
  }

  const limitar = (p: Vec2): Vec2 => ({
    x: Math.min(vb.x + vb.w, Math.max(vb.x, p.x)),
    y: Math.min(vb.y + vb.h, Math.max(vb.y, p.y)),
  });

  let arrasto: {
    readonly tipo: 'mover' | 'girar';
    readonly ini: Vec2;
    readonly pos0: Vec2;
  } | null = null;
  const comecar =
    (tipo: 'mover' | 'girar', alvo: SVGElement) =>
    (e: PointerEvent): void => {
      e.preventDefault();
      arrasto = { tipo, ini: paraModelo(e), pos0: { ...pos } };
      g.classList.add('is-arrastando');
      try {
        alvo.setPointerCapture(e.pointerId);
      } catch {
        // sem captura (ambiente de teste)
      }
    };
  corpo.addEventListener('pointerdown', comecar('mover', corpo));
  giro.addEventListener('pointerdown', comecar('girar', giro));
  g.addEventListener('pointermove', (e) => {
    if (!arrasto) return;
    const m = paraModelo(e);
    if (arrasto.tipo === 'mover') {
      pos = limitar({
        x: arrasto.pos0.x + m.x - arrasto.ini.x,
        y: arrasto.pos0.y + m.y - arrasto.ini.y,
      });
    } else {
      ang = Math.round(((Math.atan2(m.y - pos.y, m.x - pos.x) * 180) / Math.PI) * 2) / 2;
    }
    desenhar();
  });
  const soltar = (): void => {
    if (!arrasto) return;
    arrasto = null;
    g.classList.remove('is-arrastando');
    aoSoltar?.(ang);
  };
  g.addEventListener('pointerup', soltar);
  g.addEventListener('pointercancel', soltar);

  const SETAS: Readonly<Record<string, readonly [number, number]>> = {
    ArrowLeft: [-1, 0],
    ArrowRight: [1, 0],
    ArrowUp: [0, 1],
    ArrowDown: [0, -1],
  };
  g.addEventListener('keydown', (e) => {
    const d = SETAS[e.key];
    const passo = e.shiftKey ? E : E / sub;
    if (d) {
      pos = limitar({ x: pos.x + d[0] * passo, y: pos.y + d[1] * passo });
    } else if (e.key === 'q' || e.key === 'Q' || e.key === 'PageUp') {
      ang = normalizarAngulo(ang + (e.shiftKey ? 15 : 1));
    } else if (e.key === 'e' || e.key === 'E' || e.key === 'PageDown') {
      ang = normalizarAngulo(ang - (e.shiftKey ? 15 : 1));
    } else {
      return;
    }
    e.preventDefault();
    desenhar();
    aoSoltar?.(ang);
  });

  desenhar();

  // Sem instrução visível abaixo do mapa: o jeito de usar (arrastar, girar, teclado)
  // fica no rótulo acessível da própria régua, lido por quem precisa dele.
  return {
    posicao: () => ({ inicio: { ...pos }, angulo: ang }),
    dispose() {
      arrasto = null;
    },
  };
}

/**
 * Um ângulo em graus, trazido para (−180, 180] — o intervalo que o arrasto já produz.
 * Pelo teclado a soma não tinha limite, e o leitor de tela anunciava uma régua
 * "girada 390 graus".
 */
export function normalizarAngulo(a: number): number {
  const r = ((((a + 180) % 360) + 360) % 360) - 180;
  return r === -180 ? 180 : r;
}
